import { useMemo, useState } from "react";
import { SkippedOrderRegionGroup } from "./useSkippedOrders";

export interface UseSkippedOrdersSelection {
  selectedIds: Set<string>;
  selectedCount: number;
  isAllSelected: boolean;
  toggleAll: () => void;
  isRegionSelected: (regionId: string) => boolean;
  isRegionPartiallySelected: (regionId: string) => boolean;
  getRegionSelectedCount: (regionId: string) => number;
  toggleRegion: (regionId: string) => void;
  isOrderSelected: (locationId: string) => boolean;
  toggleOrder: (regionId: string, locationId: string) => void;
  clear: () => void;
}

/**
 * Three-level select/deselect (all, per-region, per-order) for the Skipped
 * Orders modal. Keyed by regionId -> Set<locationId> rather than one flat
 * Set so "is this region fully/partially selected" (for the region
 * checkbox's checked/indeterminate state) doesn't require re-scanning every
 * other region on each render.
 */
const useSkippedOrdersSelection = (
  regionGroups: SkippedOrderRegionGroup[],
): UseSkippedOrdersSelection => {
  const [selectedByRegion, setSelectedByRegion] = useState<
    Map<string, Set<string>>
  >(new Map());

  const totalCount = useMemo(
    () => regionGroups.reduce((sum, group) => sum + group.locations.length, 0),
    [regionGroups],
  );

  const selectedCount = useMemo(
    () =>
      Array.from(selectedByRegion.values()).reduce(
        (sum, ids) => sum + ids.size,
        0,
      ),
    [selectedByRegion],
  );

  const isAllSelected = totalCount > 0 && selectedCount === totalCount;

  const isRegionSelected = (regionId: string): boolean => {
    const region = regionGroups.find((group) => group.regionId === regionId);
    const selectedIds = selectedByRegion.get(regionId);
    return (
      !!region &&
      region.locations.length > 0 &&
      selectedIds?.size === region.locations.length
    );
  };

  const isRegionPartiallySelected = (regionId: string): boolean => {
    const selectedIds = selectedByRegion.get(regionId);
    return !!selectedIds?.size && !isRegionSelected(regionId);
  };

  // Powers the "N selected" chip next to each region name (per Figma node
  // 14170-86848) - only rendered by the caller when this is > 0.
  const getRegionSelectedCount = (regionId: string): number =>
    selectedByRegion.get(regionId)?.size ?? 0;

  const toggleAll = () => {
    if (isAllSelected) {
      setSelectedByRegion(new Map());
      return;
    }

    setSelectedByRegion(
      new Map(
        regionGroups.map((group) => [
          group.regionId,
          new Set(group.locations.map((location) => location.locationId)),
        ]),
      ),
    );
  };

  const toggleRegion = (regionId: string) => {
    const region = regionGroups.find((group) => group.regionId === regionId);
    if (!region) return;

    setSelectedByRegion((prev) => {
      const next = new Map(prev);
      if (isRegionSelected(regionId)) {
        next.delete(regionId);
      } else {
        next.set(
          regionId,
          new Set(region.locations.map((location) => location.locationId)),
        );
      }
      return next;
    });
  };

  const isOrderSelected = (locationId: string): boolean =>
    Array.from(selectedByRegion.values()).some((ids) => ids.has(locationId));

  const toggleOrder = (regionId: string, locationId: string) => {
    setSelectedByRegion((prev) => {
      const next = new Map(prev);
      const current = new Set(next.get(regionId) ?? []);

      if (current.has(locationId)) {
        current.delete(locationId);
      } else {
        current.add(locationId);
      }

      if (current.size === 0) {
        next.delete(regionId);
      } else {
        next.set(regionId, current);
      }
      return next;
    });
  };

  const clear = () => setSelectedByRegion(new Map());

  const selectedIds = useMemo(
    () => new Set(Array.from(selectedByRegion.values()).flatMap((s) => [...s])),
    [selectedByRegion],
  );

  return {
    selectedIds,
    selectedCount,
    isAllSelected,
    toggleAll,
    isRegionSelected,
    isRegionPartiallySelected,
    getRegionSelectedCount,
    toggleRegion,
    isOrderSelected,
    toggleOrder,
    clear,
  };
};

export default useSkippedOrdersSelection;
